import React, { useMemo } from "react";
import { Box, Typography, Paper, Chip, Divider, FormControlLabel, Switch, CircularProgress } from "@material-ui/core";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import PersonIcon from "@material-ui/icons/Person";
import moment from "moment";
import { D11 } from "./contact360.constants";

// D8.4.4a — Tab "Citas" (presentacional; recibe appointments/kpis/classes por props).

const STATUS_LABEL = {
  pending: "Pendiente confirmar",
  confirmed: "Confirmada",
  rescheduled: "Reprogramada",
  in_progress: "En curso",
  completed: "Completada",
  no_show: "No asistió",
  cancelled: "Cancelada",
  canceled: "Cancelada"
};

function statusChipStyle(status) {
  const s = (status || "").toLowerCase();
  if (s === "completed") return { backgroundColor: "rgba(0, 200, 83, 0.15)", color: "#1b5e20", border: `1px solid ${D11.green}` };
  if (s === "cancelled" || s === "canceled") return { backgroundColor: "rgba(211, 47, 47, 0.1)", color: "#c62828", border: "1px solid #ef5350" };
  if (s === "pending") return { backgroundColor: "rgba(255, 152, 0, 0.15)", color: "#e65100", border: `1px solid ${D11.orange}` };
  if (s === "confirmed") return { backgroundColor: "rgba(0, 200, 83, 0.12)", color: "#2e7d32", border: `1px solid ${D11.green}` };
  return { backgroundColor: "#f5f5f5", color: "#424242", border: "1px solid #e0e0e0" };
}

function AppointmentRowCard({ row, highlight }) {
  const label = STATUS_LABEL[row.status] || row.status || "—";
  const fmt = moment(row.appointmentDate).format("DD/MM/YYYY HH:mm");
  const svc =
    row.serviceName != null && typeof row.serviceName !== "object"
      ? String(row.serviceName)
      : "Servicio sin nombre";
  const prof =
    row.professionalName != null && typeof row.professionalName !== "object"
      ? String(row.professionalName)
      : "—";
  return (
    <Paper
      variant="outlined"
      style={{
        padding: 12,
        marginBottom: 10,
        borderRadius: 8,
        ...(highlight ? { backgroundColor: "rgba(0, 86, 179, 0.06)", borderColor: D11.blue } : {})
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap">
        <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
          {svc}
        </Typography>
        <Box display="flex" alignItems="center" style={{ gap: 6 }}>
          {row.bookedByAi ? (
            <Chip size="small" label="IA" style={{ height: 22, backgroundColor: D11.green, color: "#fff" }} />
          ) : null}
          <Chip size="small" label={label} style={{ height: 24, ...statusChipStyle(row.status) }} />
        </Box>
      </Box>
      <Box display="flex" alignItems="center" mt={1} style={{ gap: 6, color: D11.blue }}>
        <CalendarTodayIcon style={{ fontSize: 18 }} />
        <Typography variant="body2">{fmt}</Typography>
        {row.durationMin ? (
          <Typography variant="caption" color="textSecondary">
            · {row.durationMin} min
          </Typography>
        ) : null}
      </Box>
      <Box display="flex" alignItems="center" mt={0.5} style={{ gap: 6 }}>
        <PersonIcon style={{ fontSize: 18, color: D11.placeholder }} />
        <Typography variant="body2">{prof}</Typography>
      </Box>
    </Paper>
  );
}

export default function Contact360CitasTab({ appointments, kpis, classes, excludeFromSequences, savingExclude, onExcludeChange }) {
  const proximaId = kpis?.proximaCita?.id;
  const proximaRow = useMemo(
    () => (proximaId ? appointments.find((a) => a.id === proximaId) : null),
    [appointments, proximaId]
  );

  const completedCount = useMemo(
    () => appointments.filter((a) => (a.status || "").toLowerCase() === "completed").length,
    [appointments]
  );

  const cancelledCountUi = useMemo(
    () =>
      appointments.filter((a) => {
        const s = (a.status || "").toLowerCase();
        return s === "cancelled" || s === "canceled";
      }).length,
    [appointments]
  );

  const historialRows = useMemo(() => {
    const nowMs = Date.now();
    return [...appointments]
      .filter((a) => {
        if (proximaId != null && a.id === proximaId) return false;
        const t = new Date(a.appointmentDate).getTime();
        const s = (a.status || "").toLowerCase();
        const terminated = s === "completed" || s === "cancelled" || s === "canceled" || s === "no_show";
        return t < nowMs || terminated;
      })
      .sort((a, b) => new Date(b.appointmentDate) - new Date(a.appointmentDate));
  }, [appointments, proximaId]);

  return (
    <Box mt={2}>
      <Box display="flex" flexWrap="wrap" style={{ gap: 10 }} mb={2}>
        <Paper className={classes.kpiPaper} variant="outlined">
          <Typography variant="caption">Total citas</Typography>
          <Typography style={{ fontWeight: 700, fontSize: 18, color: D11.blue }}>{appointments.length}</Typography>
        </Paper>
        <Paper className={classes.kpiPaper} variant="outlined">
          <Typography variant="caption">Completadas</Typography>
          <Typography style={{ fontWeight: 700, fontSize: 18, color: D11.green }}>{completedCount}</Typography>
        </Paper>
        <Paper className={classes.kpiPaper} variant="outlined">
          <Typography variant="caption">Canceladas</Typography>
          <Typography style={{ fontWeight: 700, fontSize: 18, color: "#c62828" }}>{cancelledCountUi}</Typography>
        </Paper>
      </Box>

      <Typography variant="subtitle2" style={{ fontWeight: 700, letterSpacing: 0.6, color: D11.blue, marginBottom: 8 }}>
        PRÓXIMA CITA
      </Typography>
      {proximaRow ? (
        <AppointmentRowCard row={proximaRow} highlight />
      ) : (
        <Typography variant="body2" style={{ fontStyle: "italic", color: D11.placeholder, marginBottom: 16 }}>
          Sin próxima cita programada
        </Typography>
      )}

      <Divider style={{ margin: "16px 0" }} />

      <Typography variant="subtitle2" style={{ fontWeight: 700, letterSpacing: 0.6, color: D11.blue, marginBottom: 8 }}>
        HISTORIAL DE CITAS
      </Typography>
      {historialRows.length === 0 ? (
        <Typography variant="body2" style={{ fontStyle: "italic", color: D11.placeholder }}>
          Sin citas en historial
        </Typography>
      ) : (
        historialRows.map((row) => <AppointmentRowCard key={row.id} row={row} />)
      )}

      <Divider style={{ margin: "20px 0" }} />

      <Typography variant="subtitle2" style={{ fontWeight: 700, letterSpacing: 0.6, color: D11.placeholder, marginBottom: 8 }}>
        SECUENCIAS AUTOMÁTICAS
      </Typography>
      <Paper variant="outlined" style={{ padding: 16, borderRadius: 8, backgroundColor: "#fafafa" }}>
        <Typography variant="body2" style={{ marginBottom: 12, color: "#616161" }}>
          Detalle de envíos programados se ampliará con datos de automatización. Puede excluir al contacto de mensajes
          automáticos de secuencia.
        </Typography>
        <FormControlLabel
          control={
            <Switch
              color="primary"
              checked={!!excludeFromSequences}
              onChange={(e) => onExcludeChange(e.target.checked)}
              disabled={savingExclude}
            />
          }
          label={
            <Box>
              <Typography variant="body2" style={{ fontWeight: 600 }}>
                Excluir de secuencias
              </Typography>
              <Typography variant="caption" style={{ color: D11.placeholder }}>
                No enviar mensajes automáticos a este número (alcance global, B6).
              </Typography>
            </Box>
          }
        />
        {savingExclude ? (
          <Box mt={1} display="flex" alignItems="center" style={{ gap: 8 }}>
            <CircularProgress size={18} />
            <Typography variant="caption">Guardando…</Typography>
          </Box>
        ) : null}
      </Paper>
    </Box>
  );
}
